import React, { useCallback, useEffect, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { AdminLayout } from '../components/AdminSidebar';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';

const STATUS_TABS = [
  { id: 'all', label: 'All overrides' },
  { id: 'active', label: 'Active' },
  { id: 'expired', label: 'Expired' },
  { id: 'terminated', label: 'Terminated' },
];

function StatusPill({ status, active }) {
  const map = {
    active: { bg: 'rgba(164,195,154,0.18)', fg: '#3F6B45', border: '#A4C39A' },
    expired: { bg: 'rgba(197,160,89,0.18)', fg: '#8B6A3E', border: '#C5A059' },
    terminated: { bg: 'rgba(180,90,90,0.18)', fg: '#7A3939', border: '#B45A5A' },
  };
  const k = !active && status === 'active' ? 'expired' : status;
  const c = map[k] || map.expired;
  return (
    <span
      data-testid={`override-status-pill-${k}`}
      style={{
        backgroundColor: c.bg, color: c.fg,
        border: `1px solid ${c.border}`,
        padding: '4px 10px', borderRadius: 999,
        fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
      }}
    >
      {k}
    </span>
  );
}

function DaysRemaining({ row }) {
  const d = row.days_remaining;
  if (d == null) return <span style={{ color: '#8C8273' }}>—</span>;
  let color = '#3F6B45';
  if (d <= 0) color = '#7A3939';
  else if (d <= 30) color = '#8B6A3E';
  return (
    <span data-testid={`override-days-${row.memorial_partner_id}`}
          style={{ fontWeight: 600, color }}>
      {d <= 0 ? `${Math.abs(d)} days ago` : `${d} days`}
    </span>
  );
}

function ConflictBadge({ conflicts, mpId }) {
  if (!conflicts || conflicts.length === 0) return null;
  return (
    <span
      data-testid={`override-conflict-${mpId}`}
      title={conflicts.map(c => `${c.other_business_name || c.other_memorial_partner_id} (${c.matches.join(', ')})`).join(' · ')}
      style={{
        backgroundColor: '#FCE6CC', color: '#7A3939',
        border: '1px solid #C5A059', padding: '2px 8px', borderRadius: 6,
        fontSize: 11, fontWeight: 600,
      }}
    >
      {conflicts.length} conflict{conflicts.length > 1 ? 's' : ''}
    </span>
  );
}

function ActionButton({ children, onClick, variant = 'default', testid }) {
  const styles = {
    default: { bg: '#1A3626', color: '#F4E5C2' },
    danger: { bg: '#7A3939', color: '#FCE6CC' },
    gold: { bg: '#C5A059', color: '#1A3626' },
  };
  const s = styles[variant];
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        backgroundColor: s.bg, color: s.color,
        padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600,
        border: 'none', cursor: 'pointer', marginRight: 6,
      }}
    >
      {children}
    </button>
  );
}

export default function AdminOverrideContracts() {
  const [status, setStatus] = useState('all');
  const [rows, setRows] = useState([]);
  const [duration, setDuration] = useState(365);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/partners/overrides?status=${status}`);
      setRows(data.overrides || []);
      setDuration(data.duration_days || 365);
    } catch (err) {
      toast.error(formatApiError(err) || 'Failed to load overrides');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const onExtend = async (row) => {
    const monthsStr = window.prompt(
      `Extend the override for ${row.memorial_partner_name}.\n\nEnter the number of months to add (1-60):`,
      '12',
    );
    if (!monthsStr) return;
    const months = parseInt(monthsStr, 10);
    if (Number.isNaN(months) || months < 1 || months > 60) {
      toast.error('Please enter a number between 1 and 60');
      return;
    }
    const reason = window.prompt('Reason for the extension (required for audit log):');
    if (!reason || !reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    try {
      await api.post(`/admin/partners/overrides/${row.memorial_partner_id}/extend`, {
        months, reason: reason.trim(),
      });
      toast.success(`Override extended by ${months} months`);
      load();
    } catch (err) {
      toast.error(formatApiError(err) || 'Failed to extend');
    }
  };

  const onTerminate = async (row) => {
    if (!window.confirm(
      `Terminate the override for ${row.memorial_partner_name}?\n\nThe Memorial Partner will keep earning their $100 direct commission, but the Regional Partner above them will stop earning the $25 override immediately. This is reversible only by reassignment.`,
    )) return;
    const reason = window.prompt('Reason for termination (required for audit log):');
    if (!reason || !reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    try {
      await api.post(`/admin/partners/overrides/${row.memorial_partner_id}/terminate`, {
        reason: reason.trim(),
      });
      toast.success('Override terminated');
      load();
    } catch (err) {
      toast.error(formatApiError(err) || 'Failed to terminate');
    }
  };

  const onReassign = async (row) => {
    const rpId = window.prompt(
      `Reassign ${row.memorial_partner_name} to a different Regional Partner.\n\nEnter the new Regional Partner user ID:`,
    );
    if (!rpId) return;
    const reset = window.confirm(
      'Reset the 12-month window to start fresh?\n\nOK = reset (new RP earns override for 12 months)\nCancel = preserve original expiration (default)',
    );
    const reason = window.prompt('Reason for reassignment (required for audit log):');
    if (!reason || !reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    try {
      await api.post(`/admin/partners/overrides/${row.memorial_partner_id}/reassign`, {
        regional_partner_id: rpId.trim(),
        reset_window: reset,
        reason: reason.trim(),
      });
      toast.success('Override reassigned');
      load();
    } catch (err) {
      toast.error(formatApiError(err) || 'Failed to reassign');
    }
  };

  return (
    <div data-testid="admin-override-contracts-page" style={{ minHeight: '100vh', backgroundColor: '#F7F3E8' }}>
      <Navbar />
      <AdminLayout>
        <header style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 32, color: '#1A3626', fontWeight: 600 }}>
            Override Contracts
          </h1>
          <p style={{ color: '#5C5345', fontSize: 14, marginTop: 6 }}>
            Each Memorial Partner approval starts a {duration}-day window during which the Regional
            Partner above earns $25 every time the MP enrols a family. After the window closes the
            Memorial Partner still earns their own $100 direct commission.
          </p>
        </header>

        <nav style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {STATUS_TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`override-tab-${t.id}`}
              onClick={() => setStatus(t.id)}
              style={{
                padding: '8px 16px', borderRadius: 999, fontSize: 14, fontWeight: 600,
                backgroundColor: status === t.id ? '#1A3626' : 'transparent',
                color: status === t.id ? '#F4E5C2' : '#1A3626',
                border: `1px solid ${status === t.id ? '#1A3626' : 'rgba(26,54,38,0.3)'}`,
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {loading ? (
          <div data-testid="override-loading" style={{ color: '#5C5345', padding: 24 }}>
            Loading overrides…
          </div>
        ) : rows.length === 0 ? (
          <div data-testid="override-empty"
               style={{ color: '#5C5345', padding: 24, textAlign: 'center',
                        border: '1px dashed rgba(26,54,38,0.2)', borderRadius: 8 }}>
            No override contracts match the selected filter.
          </div>
        ) : (
          <div data-testid="override-table-wrapper" style={{
            backgroundColor: '#FFFBF0', borderRadius: 12,
            border: '1px solid rgba(26,54,38,0.15)', overflow: 'auto',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ backgroundColor: '#1A3626', color: '#F4E5C2' }}>
                <tr>
                  {['Memorial Partner', 'Regional Partner', 'Start',
                    'Expires', 'Days remaining', 'Status', 'Conflicts', 'Actions']
                    .map((h) => (
                      <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6 }}>{h}</th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.memorial_partner_id} data-testid={`override-row-${row.memorial_partner_id}`}
                      style={{ borderTop: '1px solid rgba(26,54,38,0.08)' }}>
                    <td style={{ padding: '14px' }}>
                      <div style={{ color: '#1A3626', fontWeight: 600 }}>
                        {row.memorial_partner_name || row.memorial_partner_id}
                      </div>
                      <div style={{ color: '#8C8273', fontSize: 11 }}>{row.memorial_partner_email}</div>
                    </td>
                    <td style={{ padding: '14px' }}>
                      <div style={{ color: '#1A3626' }}>{row.regional_partner_name || '—'}</div>
                      <div style={{ color: '#8C8273', fontSize: 11 }}>{row.regional_partner_id || ''}</div>
                    </td>
                    <td style={{ padding: '14px', color: '#5C5345' }}>
                      {row.override_start_date ? new Date(row.override_start_date).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '14px', color: '#5C5345' }}>
                      {row.override_expiration_date ? new Date(row.override_expiration_date).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '14px' }}>
                      <DaysRemaining row={row} />
                    </td>
                    <td style={{ padding: '14px' }}>
                      <StatusPill status={row.override_status} active={row.active} />
                    </td>
                    <td style={{ padding: '14px' }}>
                      <ConflictBadge conflicts={row.conflicts} mpId={row.memorial_partner_id} />
                    </td>
                    <td style={{ padding: '14px' }}>
                      <ActionButton variant="default"
                                     testid={`override-extend-${row.memorial_partner_id}`}
                                     onClick={() => onExtend(row)}>
                        Extend
                      </ActionButton>
                      {row.override_status !== 'terminated' && (
                        <ActionButton variant="danger"
                                       testid={`override-terminate-${row.memorial_partner_id}`}
                                       onClick={() => onTerminate(row)}>
                          Terminate
                        </ActionButton>
                      )}
                      <ActionButton variant="gold"
                                     testid={`override-reassign-${row.memorial_partner_id}`}
                                     onClick={() => onReassign(row)}>
                        Reassign
                      </ActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminLayout>
    </div>
  );
}
